import React, { useEffect, useState } from "react";
import { useHistory } from "@core/routing/useHistory";
import { Avatar, ChannelListItemUIProps, useChatContext } from "stream-chat-react";
import styled from "styled-components";
import { Environment } from "@core/config/environment";
import { Path } from "@core/routing/path";
import { truncString } from "@utils/strings";
import { Description } from "@ui/components/containers/description";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { MessageReadIcon } from "@ui/components/icons";
import { Stack, row } from "@ui/components/layout";

interface CustomChannelListPreviewProps extends ChannelListItemUIProps {
	isChatMessage?: boolean;
	profileId?: string;
	close?: () => void;
}
export const ChannelListPreview: React.FC<CustomChannelListPreviewProps> = ({
	channel,
	setActiveChannel,
	isChatMessage,
	profileId,
	close,
}) => {
	// const [unreadCount];

	const { channel: activeChannel, client } = useChatContext();

	const selected = channel?.id === activeChannel?.id;

	const members = Object.values(channel?.state?.members).filter(({ user }) => user?.id !== client.userID);

	const { user } = members[0];

	if (!channel.state.messages.length) return null;

	const lastMessage = channel.state.messages[channel.state.messages.length - 1];

	const [unReadCount, setUnReadCount] = useState(channel.state.unreadCount);

	useEffect(() => {
		if (!selected) {
			setUnReadCount(channel.state.unreadCount);
		}
	}, [channel.state, channel.state.unreadCount]);

	const messageTimestamp = lastMessage?.created_at; // Assuming the timestamp is in created_at;

	const history = useHistory();

	return (
		<Container
			onClick={() => {
				if (isChatMessage) {
					setActiveChannel?.(channel);
					setUnReadCount(0);
					return;
				} else {
					if (profileId && close) {
						history.push(Path.Chatmessage(profileId, channel?.data?.id as string));
						close();
					}
				}
			}}
			selected={selected}
			isNewMessage={unReadCount > 0}
			isChatMessage={isChatMessage}
		>
			<Row>
				<Avatar imageUrl={user?.image} userName={user?.name} size="lg" />
				<Stack>
					<StyledPrimary>{user?.name}</StyledPrimary>
					<StyledDescription>{truncString(lastMessage?.text as string, 40) || "message text"}</StyledDescription>
				</Stack>
				{/* <OnlineBloc /> */}
				{user?.online ? <OnlineBloc /> : null}
			</Row>
			<Bloc>
				{messageTimestamp && (
					<Date>{messageTimestamp.toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}</Date>
				)}
				<MessageReadIcon />
			</Bloc>
			{unReadCount > 0 ? <Count>{unReadCount}</Count> : null}
		</Container>
	);
};

const Container = styled.div.withConfig({
	shouldForwardProp: (prop) => !["selected", "isNewMessage", "isChatMessage"].includes(prop),
})<{ selected?: boolean; isNewMessage?: boolean; isChatMessage?: boolean }>`
	${row(0, "space-between", "flex-start")}
	background-color: ${({ selected, isNewMessage, theme }) =>
		Environment.APP_VARIANT === "jaangal"
			? isNewMessage
				? theme.colors.primaryLight
				: selected
					? theme.colors.primaryLightTouch
					: theme.colors.white
			: isNewMessage
				? "rgba(243, 234, 236, 1)"
				: selected
					? "rgba(210, 225, 252, 0.37)"
					: theme.colors.white};
	transition: background 0.1s ease-in-out;
	width: 100%;
	padding: 20px;
	position: relative;
	border-bottom: solid 1px #f7f6f6;
	box-sizing: border-box; /* Assurez-vous que la largeur inclut le padding et la bordure */
	cursor: pointer;

	&:hover {
		background-color: ${({ theme }) => theme.colors.primaryLight};
	}

	@media (max-width: 768px) {
		padding: 15px;
		// width:80%;
	}

	.str-chat__avatar {
		background-color: ${({ theme }) => (Environment.APP_VARIANT === "jaangal" ? theme.colors.primary : "#bf360c")};
		color: white;

		&::after {
			content: none;
		}
	}
`;

const Row = styled.div`
	${row(8, "center", "flex-start")};
	position: relative;
	cursor: pointer; /* Default cursor as hand */
	transition: cursor 0.2s ease-in-out; /* Smooth cursor transition */

	&:hover {
		cursor: default; /* Restore default cursor on hover */
	}

	> *:not(.str-chat__avatar) {
		text-decoration: none;
		display: block;
	}

	> *:hover {
		cursor: pointer; /* Show hand cursor on hover over list items */
	}
`;

const StyledPrimary = styled(PrimaryMedium)`
	color: #000;
	font-family: Sora;
	font-size: 14px;
	font-style: normal;
	font-weight: 600;
	line-height: normal;
`;

const StyledDescription = styled(Description)`
	color: #000;
	font-size: 12px;
	font-style: normal;
	font-weight: 400;
	line-height: normal;
`;

const Date = styled.div`
	color: #979797;
	font-size: 9px;
	font-style: normal;
	font-weight: 500;
	line-height: normal;
`;

const Bloc = styled(Stack)`
	justify-content: space-between;
	height: 100%;
	align-items: flex-end;
	margin-right: 2px;
`;

const Count = styled.div`
	width: 12px;
	height: 12px;
	background-color: rgba(232, 54, 54, 1);
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	color: #fff;
	position: absolute;
	right: 6px;
	top: 4px;
	font-size: 12px;
	font-weight: 500;
`;

const OnlineBloc = styled.div`
	width: 12px;
	height: 12px;
	border-radius: 50%;
	background-color: rgba(16, 195, 116, 1);
	position: absolute;
	left: 30px;
	bottom: 0px;
`;
